/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { format, parseISO } from "date-fns";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import Bugsnag from "@bugsnag/js";
import { Gift, ChevronDown, ChevronUp, ExternalLink, Play } from "lucide-react";
import api from "@/lib/axios";
import wiwiExampleImage from "../assets/wiwi-example.png";

const logoImage = "https://wiwi.gifts/images/wiwi-logo.svg";

// שאלות נפוצות
const faqData = [
  {
    question: "מה זה רשומה?",
    answer:
      "מאוד פשוט - רשומה היא מספר טלפון אחד 📱 ולא כל מוזמן בנפרד. כאשר רוצים להזמין זוג או זוג עם ילדים קטנים, רושמים מספר טלפון של ראש המשפחה, והוא יקבל את אישור ההגעה ויעדכן כמה מגיעים מאותו תא משפחתי.\nלכן מתוך 300 מוזמנים יהיו בערך כ־200 רשומות, ואת התשלום מבצעים רק על מי שעונה בפועל 🙂.",
  },
  {
    question: "מה זה אומר 'על בסיס הצלחה'?",
    answer:
      "זה אומר בקצרה שאתם משלמים רק על מספר טלפון שעונה לנו – 'מגיע' / 'לא מגיע' / 'מתלבט'. מי שלא עונה כלל לא ייחשב לתשלום.\nנעשה את המאמץ הכי גדול על מנת שכולם יענו, ובכך נעזור לכם לחסוך מנות ולחץ מיותר (כמובן בלי לשגע את המוזמנים 😉).",
  },
  {
    question: "כמה סבבים קורים בפועל? איך זה מתבצע?",
    answer:
      "אין הגבלה על מספר הסבבים 🔄, מאחר שהשירות פועל על בסיס הצלחה - המטרה משותפת!\nאתם קובעים מתי מתחילים ומתי תרצו תשובות סופיות לאולם, וכל השאר מתבצע על ידינו.\nבאפליקציה ניתן לראות את זמני הסבבים ואת התגובות בזמן אמת 📲.",
  },
  {
    question: "מתי מתחילים את האישורי הגעה?",
    answer:
      "אתם קובעים מתי נתחיל ומתי נסיים 🙂\nמומלץ להתחיל כשבועיים–שלושה לפני האירוע ולסיים כ־5–7 ימים לפני 🗓️",
  },
  {
    question: "מה קורה אם לאורח אין וואטסאפ?",
    answer:
      "המערכת מזהה זאת אוטומטית, ומשנה עבורו את ההודעות ל־SMS ✉️",
  },
  {
    question: "האולם שלי עובד עם iPlan / InviteMe / SeatUp - זה מתממשק?",
    answer:
      "זו לא בעיה כלל, ועל כך יעידו מאות אירועים כל חודש שלנו שעושים בדיוק את אותו הדבר! 🤝\nבאפליקציה ניתן לייצא בלחיצת כפתור קובץ מותאם לשלוש המערכות ולבצע הושבה מול האולם בקלות",
  },
  {
    question: "מהי עמלת הסליקה על שירות המתנות באשראי?",
    answer:
      "נכון להיום, עמלת הסליקה של האורח היא 2.7% מסך המתנה 💳\nלדוגמה: מתנה של 400 ש\"ח → האורח ישלם 410.7 ש\"ח.\nהאורח מודע לעמלה ולגובה התשלום -\nובשום מקרה העמלה לא גורעת מהמתנה שלכם ולא יורדת מכם! 🎁✨\n\nהעמלה יכולה להשתנות בהתאם לתקנון השירות",
  },
  {
    question: "איך מתבצע התשלום אליכם? כמה זה עולה בפועל?",
    answer:
      "התשלום הראשוני הוא מקדמה של 200 ש\"ח, המשמשת כתשלום מינימום עבור השירות, ו־159 ש\"ח עבור שירות המתנות באשראי (אם תרצו להוסיף 😇).\nיום לאחר האירוע המערכת מחשבת: כמות הרשומות המוצלחות × המחיר לרשומה, ומקזזת את המקדמה מהסכום הסופי.\nאם הסכום הסופי גבוה מהמקדמה - תחויבו רק בהפרש. אם הסכום הסופי נמוך מהמקדמה - לא יהיה חיוב נוסף, אך המקדמה אינה ניתנת להחזר.\nהתשלום מתבצע בכרטיס אשראי 💳",
  },
  {
    question: "איך אני רואה את התשובות ומעלה את האורחים? יש מערכת מסודרת?",
    answer:
      "יש אפליקציה מסודרת 📲💻 שניתן לגשת אליה בכל רגע ולראות הכול בלייב.\nניתן להעלות מוזמנים דרך אקסל או דרך אנשי קשר בוואטסאפ.\nגם תמונה/סרטון של ההזמנה ניתן להעלות ישירות לאפליקציה",
  },
  {
    question: "האם השיחות מבוצעות על־ידי נציגות אנושיות ולא בוטים?",
    answer:
      "כן! 100% נציגות אנושיות 👩💼\nכל סבבי השיחות מבוצעים על־ידי צוות מקצועי - בלי בוטים בכלל",
  },
  {
    question: "מה זה סבב הזמנה קולית?",
    answer:
      "זהו סבב שבו יוצאת שיחה עם הקלטה אנושית 🎤 של ההזמנה שלכם (לא בוט!).\nהסבב לא מחליף שיחות אנושיות אלא נוסף אליהן, ומתאים במיוחד לטלפונים כשרים, מבוגרים או אנשים שפחות ניגשים לנייד 📞",
  },
  {
    question: "איך פונים לשירות לקוחות?",
    answer:
      "יש שירות לקוחות ותמיכה טכנית בימים ראשון עד חמישי בין 9:00–17:00 ☎️\nעם נציגים אנושיים שזמינים לכל שאלה",
  },
  {
    question: "איך אדע מתי יוצא כל סבב?",
    answer:
      "באפליקציה מופיע \"לוח תזמונים\" 📆 עם כל הסבבים לפי תאריך ושעה",
  },
  {
    question: "האם אראה את ההודעות לפני שהן יוצאות?",
    answer:
      "בהחלט!\n24 שעות לפני כל סבב תקבלו תזכורת + דוגמה של ההודעה שתישלח.\nההודעות ניתנות לעריכה חופשית - וברגע שתערכו ותשמרו, תקבלו את הנוסח ישירות לנייד 📲\nברגע שתערכו את ההודעה ותבצעו שמירה - תקבלו דוגמה שלה ישירות לנייד",
  },
  {
    question: "איך אדע אם אורחים קיבלו את ההודעה?",
    answer:
      "לכל אורח יש היסטוריית התקשרויות מלאה - תוכלו לראות אם קיבל ✔, אם קרא 👀, באיזה תאריך ושעה זה קרה, ומה הייתה תשובתו.\nבנוסף ניתן לראות כמה סבבי שיחות נעשו לכל אורח ומתי 📊",
  },
];

export default function PricingPage({ shortLink }) {
  const { packageHash } = useParams();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [packageDetails, setPackageDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsLoading, setTermsLoading] = useState(true);
  const [showFloatingButton, setShowFloatingButton] = useState(true);
  const [extending, setExtending] = useState(false);
  const [alreadyExtended, setAlreadyExtended] = useState(false);

  const eventTypeDetail = useMemo(() => {
    if (
      packageDetails &&
      packageDetails.event_types &&
      packageDetails.event_types.length > 0
    ) {
      return packageDetails.event_types[0]; // לוקח את סוג האירוע הראשון
    }
    return null;
  }, [packageDetails]);

  async function fetchPackage() {
    try {
      const res = await api.get(
        shortLink
          ? `/GetPackage?token=${packageHash}&pricing=true`
          : `/Package?token=${packageHash}&pricing=true`,
      );
      if (res.data.success) {
        Bugsnag.setUser(
          packageHash,
          "",
          shortLink ? "ShortLinkUser" : "RegularUser",
        );
        setPackageDetails(res.data.data);
      } else if (res.data.errorCode === 1064) {
        setAlreadyExtended(!res.data.data?.extendable);
      } else {
        //navigate("/PackageUnavailable");
      }
    } catch (error) {
      console.error("Error fetching package:", error);
      //navigate("/PackageUnavailable");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (packageHash) {
      fetchPackage();
    } else {
      //navigate("/PackageUnavailable");
    }
  }, [packageHash]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Hide button when user is near the bottom (within 100px)
      const isNearBottom = scrollTop + windowHeight >= documentHeight - 100;
      setShowFloatingButton(!isNearBottom);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  function GoToRegisterPage() {
    navigate(shortLink ? `/${packageHash}` : `/p/${packageHash}`);
  }

  async function extendPackage() {
    if (extending) return;
    setExtending(true);
    try {
      const res = await api.put(`/ExtendPackageExpiry?hash=${packageHash}`);
      if (res.data.success) {
        fetchPackage();
      }
    } catch (error) {
      console.error("Error extending package:", error);
    } finally {
      setExtending(false);
    }
  }

  // const whatsappUrl = `https://wa.me/972539242324?text=${encodeURIComponent(
  //   "שלום, אני מעוניין לשמוע עוד על שירות WiWi"
  // )}`;

  if (loading) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 flex items-center justify-center"
        dir="rtl"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
          <p className="text-gray-600">טוען פרטי חבילה...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 py-8 px-4 pb-16"
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <img
            src={logoImage}
            alt="WiWi Logo"
            className="h-16 md:h-20 mx-auto mb-6"
          />
          {packageDetails && (
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              {packageDetails.name}
            </h1>
          )}
        </motion.div>

        {/* Video Demo Section - Top */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-4 md:p-8 mb-8"
        >
          <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl p-4 md:p-6 border border-rose-100">
            <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 text-center">
              🎬 סרטון דוגמא
            </h3>
            <p className="text-gray-600 text-sm md:text-base mb-4 text-center">
              צפו בסרטון קצר או תמונות המערכת ותבינו בדיוק איך אנחנו עוזרים לכם
            </p>
            <div className="relative aspect-video rounded-xl overflow-hidden max-w-2xl mx-auto mb-4">
              <iframe
                src="https://www.youtube.com/embed/eqPh2WLVw2o?&autoplay=1&loop=1&rel=0&showinfo=0&color=white&iv_load_policy=3"
                title="WiWi Service Demo"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => setShowImageModal(true)}
                className="h-12 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl shadow-lg flex items-center justify-center gap-3 text-base font-semibold"
              >
                <ExternalLink className="w-5 h-5" />
               לחצו כאן לצפייה בדף אירוע
              </Button>
            </div>
            
          </div>
          
        </motion.div>

        {/* Service Details Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.33 }}
          className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-4 md:p-8 mb-8"
        >
          <div className="bg-gradient-to-r from-rose-50 to-purple-50 rounded-2xl p-4 md:p-6 mb-4 md:mb-6 border border-rose-100">
            <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-3 md:mb-4">
              ✅ מה כולל שירות אישורי ההגעה?
            </h3>

            <div className="space-y-3">
              <div className="flex items-center gap-3 text-green-700">
                <span className="text-lg md:text-xl">✔</span>
                <span className="text-sm md:text-base">
                  הודעות WhatsApp לאישור הגעה
                </span>
              </div>
              <div className="flex items-center gap-3 text-green-700">
                <span className="text-lg md:text-xl">✔</span>
                <span className="text-sm md:text-base">
                  שיחות טלפון עם נציגות <strong>אנושיות</strong>
                </span>
              </div>
              <div className="flex items-center gap-3 text-green-700">
                <span className="text-lg md:text-xl">✔</span>
                <span className="text-sm md:text-base">
                  סבב תזכורת קולית (לא על חשבון סבבי השיחות)
                </span>
              </div>
              <div className="flex items-center gap-3 text-green-700">
                <span className="text-lg md:text-xl">✔</span>
                <span className="text-sm md:text-base">
                  הודעת WhatsApp "תזכורת" בבוקר האירוע/יום לפני האירוע, כולל
                  קישור ל-Waze
                </span>
              </div>
              <div className="flex items-center gap-3 text-green-700">
                <span className="text-lg md:text-xl">✔</span>
                <span className="text-sm md:text-base">
                  הודעת WhatsApp "תודה" יום לאחר האירוע
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-2xl p-4 md:p-6 mb-4 md:mb-6 border border-pink-100">
            <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2 flex-wrap">
              ⭐ מה מיוחד אצלנו?<span className="text-xl md:text-2xl"></span>
            </h3>

            <div className="space-y-3 md:space-y-4">
              <div className="bg-white/70 rounded-xl p-3 md:p-4">
                <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                  {/* <span className="text-lg md:text-xl">💡</span> */}
                  <span className="text-sm md:text-base">
                    תשלום רק על הצלחה!
                  </span>
                </h4>
                <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                  משלמים רק על רשומה (מספר טלפון) שענתה בפועל – "מגיע", "לא
                  מגיע" או "מתלבט" – והתשלום מתבצע רק בסוף האירוע.
                </p>
                <div className="mt-2 p-2 bg-yellow-100 rounded-lg">
                  <p className="text-xs md:text-sm text-yellow-800">
                    <strong>📌לשים לב!</strong> רשומה = תא משפחתי אחד. כאשר
                    מזמינים זוג עם ילדים, מזינים את מספר הטלפון של ראש המשפחה –
                    ולכן מדובר ברשומה אחת בלבד. לדוגמה: 300 מוזמנים סה"כ יהפוך
                    לכ200 רשומות בפועל.
                  </p>
                  <p className="text-xs md:text-sm text-yellow-800 mt-1"></p>
                </div>
              </div>

              <div className="bg-white/70 rounded-xl p-3 md:p-4">
                <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                  {/* <span className="text-lg md:text-xl">📣</span> */}
                  <span className="text-sm md:text-base">אין מגבלת סבבים!</span>
                </h4>
                <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                  אין מגבלת סבבים של הודעות WhatsApp ושיחות טלפון, מכיוון
                  שהשירות פועל על בסיס הצלחה. התזמונים נקבעים בהתאם לתאריך
                  ההתחלה שתבחרו (מועד תחילת ההליך) ולתאריך הסיום שבו תצטרכו לקבל
                  את כל התשובות.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-rose-50 rounded-2xl p-4 md:p-6 border border-purple-100">
            <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-3 md:mb-4">
              🎁 פיצ'רים שנכללים בשירות
            </h3>

            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <span className="text-green-600 mt-1">•</span>
                <span className="text-gray-700 text-sm md:text-base">
                  שאלה על הסעה והוספת הערות אורח (כמו סוג מנה, רגישויות ועוד)
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-600 mt-1">•</span>
                <span className="text-gray-700 text-sm md:text-base">
                  אפשרות לצרף מספר שולחן בהודעת תזכורת
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-600 mt-1">•</span>
                <span className="text-gray-700 text-sm md:text-base">
                  ייצוא קובץ אקסל מותאם למערכות הושבה מובילות: iPlan / InviteMe
                  / SeatUp וכו'
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-600 mt-1">•</span>
                <span className="text-gray-700 text-sm md:text-base">
                  ממשק אפליקציה לניהול האורחים, שמתעדכן <strong>LIVE</strong>
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-600 mt-1">•</span>
                <span className="text-gray-700 text-sm md:text-base">
                  שליחת SMS אוטומטית לאורחים שהמערכת מזהה שאין להם WhatsApp
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Action Buttons */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid md:grid-cols-2 gap-4 mb-8"
        >
          <Button
            onClick={() => window.open("https://app.wiwi.co.il", "_blank")}
            className="h-16 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl shadow-lg flex items-center justify-center gap-3 text-lg font-semibold"
          >
            <Users className="w-6 h-6" />
            כניסה למערכת
            <ExternalLink className="w-4 h-4" />
          </Button>

          <Button
            onClick={() => window.open("https://bit.ly/wiwiytb", "_blank")}
            className="h-16 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl shadow-lg flex items-center justify-center gap-3 text-lg font-semibold"
          >
            <Play className="w-6 h-6" />
            סרטוני הדרכה
            <ExternalLink className="w-4 h-4" />
          </Button>

          <Button
            onClick={() => window.open(whatsappUrl, "_blank")}
            className="h-16 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl shadow-lg flex items-center justify-center gap-3 text-lg font-semibold md:col-span-2"
          >
            <MessageCircle className="w-6 h-6" />
            יצירת קשר עם שירות לקוחות
            <ExternalLink className="w-4 h-4" />
          </Button>
        </motion.div> */}

        {/* Terms and Conditions Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-4 md:p-8 mb-8 text-center"
        >
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 md:p-6 border border-purple-100">
            <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-3 md:mb-4">
              📋 תנאי שימוש
            </h3>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-4">
              כל השירותים והמחירים כפופים לתנאי השימוש שלנו. ממליצים לקרוא לפני
              הרשמה.
            </p>
            <Button
              onClick={() => setShowTermsModal(true)}
              className="h-12 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl shadow-lg flex items-center justify-center gap-3 text-base font-semibold mx-auto"
            >
              <ExternalLink className="w-4 h-4" />
              קריאת התנאים
            </Button>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-4 md:p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            שאלות נפוצות
          </h2>

          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full p-4 text-right bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
                >
                  <span className="font-medium text-gray-800">
                    {faq.question}
                  </span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  )}
                </button>
                {openFaq === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="p-4 bg-white border-t border-gray-200"
                  >
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {faq.answer
                        .split("**")
                        .map((part, index) =>
                          index % 2 === 1 ? (
                            <strong key={index}>{part}</strong>
                          ) : (
                            part
                          ),
                        )}
                    </div>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl border-2 border-rose-200 p-6 mb-8 shadow-sm relative"
        >
          {packageDetails ? (
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Gift className="w-6 h-6 text-rose-600" />
                <h3 className="text-xl font-bold text-rose-900">
                  הצעת מחיר - {packageDetails.name}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="bg-white/70 rounded-xl p-4 border border-rose-100 flex flex-col justify-between">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">מקדמה</div>
                    <div className="text-2xl font-bold text-rose-600">
                      {packageDetails.deposit} ש&quot;ח
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    <strong>בעת רישום לשירות, יקוזז מהתשלום הסופי</strong>
                  </p>
                </div>

                <div className="bg-white/70 rounded-xl p-4 border border-rose-100">
                  <div className="text-sm text-gray-600 mb-1">
                    מחיר לרשומה מוצלחת
                  </div>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-gray-400 line-through text-sm">
                      2.00 ₪
                    </span>
                    <span className="text-2xl font-bold text-rose-600">
                      {packageDetails.per_successful_registration} ש&quot;ח
                    </span>
                  </div>
                </div>

                <div className="bg-white/70 rounded-xl p-4 border border-rose-100">
                  <div className="text-sm text-gray-600 mb-1">
                    שירות מתנות באשראי
                  </div>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-gray-400 line-through text-sm">
                      200 ₪
                    </span>
                    <span className="text-2xl font-bold text-rose-600">
                      {eventTypeDetail?.gift_charge || "159"} ש&quot;ח
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    <strong>אופציונלי, ניתן לבחור בטופס הרישום</strong>
                  </p>
                </div>
              </div>

              <div className="mt-4 p-3 bg-rose-100/50 rounded-lg border border-rose-200">
                <p className="text-sm text-rose-800 leading-relaxed">
                  <strong>רשומה מוצלחת</strong> = רשומה (מספר טלפון) שממנה
                  התקבלה תשובה בפועל – &quot;מגיע", "לא מגיע&quot; או
                  &quot;מתלבט" – כלומר, רשומה שהצלחנו להביא לכם תשובה לגביה.
                  התשלום הסופי יתבצע רק לפי מספר הרשומות המוצלחות.
                </p>
                <p className="text-sm text-rose-800 leading-relaxed text-center font-medium mt-2">
                  ההצעה הינה אישית ולא ניתנת להעברה
                </p>
              </div>
              {packageDetails.expiry && (
                <motion.div
                  className="mt-3 p-3 bg-red-100/70 rounded-lg border border-red-200"
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{
                    repeat: Infinity,
                    duration: 3,
                    ease: "easeInOut",
                  }}
                >
                  <p className="text-sm text-red-800 font-semibold text-center">
                    {`⏰ ההצעה בתוקף עד ${format(
                      parseISO(packageDetails.expiry),
                      "dd/MM/yyyy",
                    )} בשעה ${format(parseISO(packageDetails.expiry), "HH:mm")}`}
                  </p>
                </motion.div>
              )}
            </div>
          ) : (
            <div className="text-center relative min-h-[200px] md:min-h-0">
              {/* Blurred background content */}
              <div className="blur-sm opacity-50 pointer-events-none">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Gift className="w-6 h-6 text-rose-600" />
                  <h3 className="text-xl font-bold text-rose-900">
                    הצעת מחיר - חבילת בסיס
                  </h3>
                </div>

                <div className="hidden md:block">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div className="bg-white/70 rounded-xl p-4 border border-rose-100 flex flex-col justify-between">
                      <div>
                        <div className="text-sm text-gray-600 mb-1">מקדמה</div>
                        <div className="text-2xl font-bold text-rose-600">
                          200 ש"ח
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        * יקוזז מהתשלום הסופי
                      </p>
                    </div>

                    <div className="bg-white/70 rounded-xl p-4 border border-rose-100">
                      <div className="text-sm text-gray-600 mb-1">
                        מחיר לרשומה מוצלחת
                      </div>
                      <div className="flex items-baseline justify-center gap-2">
                        <span className="text-gray-400 line-through text-sm">
                          2.00 ₪
                        </span>
                        <span className="text-2xl font-bold text-rose-600">
                          2 ש"ח
                        </span>
                      </div>
                    </div>

                    <div className="bg-white/70 rounded-xl p-4 border border-rose-100">
                      <div className="text-sm text-gray-600 mb-1">
                        שירות מתנות באשראי (אופציונלי)
                      </div>
                      <div className="flex items-baseline justify-center gap-2">
                        <span className="text-gray-400 line-through text-sm">
                          200 ₪
                        </span>
                        <span className="text-2xl font-bold text-rose-600">
                          159 ש"ח
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        <strong>אופציונלי, ניתן לבחור בטופס הרישום</strong>
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-rose-100/50 rounded-lg border border-rose-200">
                    <p className="text-sm text-rose-800 leading-relaxed">
                      <strong>רשומה מוצלחת</strong> = רשומה (מספר טלפון) שממנה
                      התקבלה תשובה בפועל – "מגיע", "לא מגיע" או "מתלבט" – כלומר,
                      רשומה שהצלחנו להביא לכם תשובה לגביה. התשלום הסופי יתבצע רק
                      לפי מספר הרשומות המוצלחות.
                    </p>
                    <p className="text-sm text-rose-800 leading-relaxed text-center font-medium mt-2">
                      ההצעה הינה אישית ולא ניתנת להעברה
                    </p>
                  </div>
                </div>
              </div>

              {/* Overlay content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center backdrop-blur-sm rounded-2xl">
                <h3 className="text-2xl font-bold text-red-600 mb-4">
                  הצעת מחיר אינה זמינה
                </h3>
                <p className="text-gray-700 mb-6 text-center max-w-md">
                  {alreadyExtended
                    ? "תוקף ההצעה פג. ניתן ליצור קשר עם נציג המכירות לקבלת הצעה מעודכנת"
                    : "ההצעה שלכם פגה תוקף. ניתן להאריך את התוקף ב-3 שעות נוספות"}
                </p>
                {!alreadyExtended && (
                  <Button
                    onClick={extendPackage}
                    disabled={extending}
                    className="h-12 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 disabled:opacity-50 text-white rounded-xl shadow-lg px-6 text-base font-semibold"
                  >
                    {extending ? "מאריך..." : "הארכת תוקף ל-3 שעות"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </motion.div>

        {/* Registration CTA */}
        {packageDetails && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-center"
          >
            <div className="bg-gradient-to-r from-rose-500 to-pink-500 rounded-2xl p-8 text-white shadow-xl">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                מוכנים להתחיל?
              </h2>
              {packageDetails?.expiry && (
                <motion.div
                  className="bg-red-500/20 rounded-lg p-3 mb-4"
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{
                    repeat: Infinity,
                    duration: 3,
                    ease: "easeInOut",
                  }}
                >
                  <p className="text-lg font-semibold">
                    {`⏰ ההצעה בתוקף עד ${format(
                      parseISO(packageDetails.expiry),
                      "dd/MM/yyyy",
                    )} בשעה ${format(parseISO(packageDetails.expiry), "HH:mm")}`}
                  </p>
                </motion.div>
              )}
              <p className="text-lg mb-6 opacity-90">
                הצטרפו לאלפי זוגות מרוצים והפכו את האירוע שלכם להרבה יותר מסודר
                ורגוע.
              </p>
              <Button
                onClick={() => GoToRegisterPage()}
                className="h-14 bg-white text-rose-600 hover:bg-gray-100 rounded-xl px-8 text-lg font-semibold shadow-lg"
              >
                התחילו רישום לשירות 👇
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Floating Registration Button */}
      {packageDetails && (
        <motion.div
          initial={{ opacity: 0, x: -100 }}
          animate={{
            opacity: 1,
            x: [0, -8, 0],
          }}
          transition={{
            x: {
              repeat: Infinity,
              duration: 0.6,
              repeatDelay: 4.4,
              ease: "easeOut",
            },
          }}
          className="hidden lg:block fixed right-6 top-1/2 transform -translate-y-1/2 z-50"
        >
          <Button
            onClick={() => GoToRegisterPage()}
            className="w-16 h-44 bg-gradient-to-b from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl shadow-lg flex flex-col items-center justify-center gap-2 font-semibold text-sm writing-mode-vertical"
            style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
          >
            התחילו רישום לשירות 👇
          </Button>
        </motion.div>
      )}

      {/* Mobile Floating Button */}
      {packageDetails && showFloatingButton && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{
            opacity: 1,
            y: [0, -8, 0],
          }}
          exit={{ opacity: 0, y: 100 }}
          transition={{
            y: {
              repeat: Infinity,
              duration: 0.6,
              repeatDelay: 4.4,
              ease: "easeOut",
            },
          }}
          className="lg:hidden fixed bottom-12 left-0 right-0 px-4 z-40"
        >
          <Button
            onClick={() => GoToRegisterPage()}
            className="w-full h-12 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl shadow-2xl flex items-center justify-center gap-2 text-sm font-semibold"
          >
            התחילו רישום לשירות 👇
          </Button>
        </motion.div>
      )}

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-sm py-2 text-center border-t border-gray-200">
        <a
          href="https://wiwi.co.il/%D7%94%D7%A6%D7%94%D7%A8%D7%AA-%D7%A0%D7%92%D7%99%D7%A9%D7%95%D7%AA/"
          className="text-sm text-black hover:text-gray-700 transition-colors"
          aria-label="Accessibility Statement"
        >
          הצהרת נגישות
        </a>{" "}
        |{" "}
        <a
          href="https://wiwi.co.il/privacy-policy/"
          className="text-sm text-black hover:text-gray-700 transition-colors"
          aria-label="Privacy Policy"
        >
          מדיניות פרטיות
        </a>
      </footer>

      {/* Video Modal */}
      {showVideoModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowVideoModal(false)}
        >
          <div
            className="bg-white rounded-2xl p-4 max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800"> </h3>
              <button
                onClick={() => setShowVideoModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="relative aspect-video rounded-xl overflow-hidden">
              <iframe
                src="https://www.youtube.com/embed/eqPh2WLVw2o"
                title="WiWi Service Demo"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowImageModal(false)}
        >
          <div
            className="bg-white rounded-2xl p-4 max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">תמונות המערכת</h3>
              <button
                onClick={() => setShowImageModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="relative rounded-xl overflow-hidden">
              <img
                src={wiwiExampleImage}
                alt="תמונת המערכת"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      )}

      {/* Terms Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white w-full h-full flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-bold text-gray-800">תנאי שימוש</h3>
              <button
                onClick={() => {
                  setShowTermsModal(false);
                  setTermsLoading(true);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="flex-1 relative">
              {termsLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">טוען תנאי שימוש...</p>
                  </div>
                </div>
              )}
              <iframe
                src="https://app.wiwi.co.il/terms"
                title="תנאי שימוש"
                className="w-full h-full border-0"
                onLoad={() => setTermsLoading(false)}
              />
            </div>
            <div className="p-4 border-t bg-gray-50">
              <Button
                onClick={() => {
                  setShowTermsModal(false);
                  setTermsLoading(true);
                }}
                className="w-full h-12 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-xl shadow-lg text-base font-semibold"
              >
                חזרה להצעת מחיר
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
PricingPage.propTypes = {
  shortLink: PropTypes.bool,
};
